import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search } from "lucide-react";

/**
 * GuessDropdown — searchable, dropdown-only wrestler picker.
 *
 * Rules:
 * - Dropdown is COMPLETELY hidden when the search field is empty. It does NOT open on
 *   focus/click. Suggestions only appear after ≥1 typed character.
 * - Server does normalization + ranking (canonical exact > alias exact > name-starts >
 *   last-name-starts > any-word-starts > partial). Ignores case, punctuation, apostrophes,
 *   accents, and treats hyphens as spaces.
 * - Excludes previously guessed wrestler ids.
 * - Max 8 results, scrollable.
 * - Requires selection before submit (Submit disabled until pick).
 * - Never accepts arbitrary typed text.
 * - Clears selection if the player edits after picking → Submit disables again.
 * - Displays only canonical names (no era/aliases/photos/clues).
 * - 150 ms debounce.
 */
export default function GuessDropdown({ excludeIds = [], disabled, onSubmit, onSkip }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [hi, setHi] = useState(0);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false); // true only after user typed at least one char
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const listRef = useRef(null);
  const debRef = useRef(null);

  const isOpen = touched && query.length > 0 && !selected;

  const fetchResults = useCallback(async (q) => {
    if (!q) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/wrestlers/search", {
        params: { q, exclude: excludeIds.join(","), limit: 8 },
      });
      setResults(data || []);
      setHi(0);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, [excludeIds]);

  useEffect(() => {
    clearTimeout(debRef.current);
    if (!query) { setResults([]); setLoading(false); return; }
    debRef.current = setTimeout(() => fetchResults(query), 150);
    return () => clearTimeout(debRef.current);
  }, [query, fetchResults]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setResults([]);
        setTouched(false); // fully close the dropdown container including empty-state
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, []);

  const pick = (w) => {
    setSelected(w);
    setQuery(w.primary_name);
    setResults([]);
    setTouched(false); // close dropdown
  };

  const submit = () => {
    if (!selected) return;
    const w = selected;
    setSelected(null);
    setQuery("");
    setResults([]);
    setTouched(false);
    onSubmit(w);
  };

  const onChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setTouched(v.length > 0);
    // if user edits after selecting, clear the selection so Submit disables
    if (selected && v !== selected.primary_name) setSelected(null);
    if (v.length === 0) { setSelected(null); setResults([]); setTouched(false); }
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (results.length) setHi((h) => Math.min(results.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (results.length) setHi((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (isOpen && results[hi]) pick(results[hi]);
      else if (selected) submit();
    } else if (e.key === "Escape") {
      setResults([]);
    }
  };

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${hi}"]`);
    if (el?.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [hi, isOpen]);

  const showEmptyState = touched && query.length > 0 && !loading && results.length === 0 && !selected;

  return (
    <div ref={wrapperRef} data-testid="guess-dropdown-wrapper">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 z-10" />
        <Input
          ref={inputRef}
          value={query}
          disabled={disabled}
          onChange={onChange}
          onKeyDown={onKey}
          placeholder="Search for a wrestler, team, or faction"
          aria-label="Search for a wrestler, team, or faction"
          role="combobox"
          aria-expanded={isOpen || showEmptyState}
          aria-controls="guess-dropdown-list"
          autoComplete="off"
          data-testid="guess-search-input"
          className="pl-9 h-12 bg-slate-950 border-2 border-slate-700 focus:border-red-500 focus-visible:ring-red-500/40 text-white text-base font-medium rounded-xl placeholder:text-slate-500"
        />
        {(isOpen || showEmptyState) && (
          <div id="guess-dropdown-list" role="listbox"
               ref={listRef}
               data-testid="guess-dropdown-list"
               className="absolute left-0 right-0 mt-2 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50 max-h-64 overflow-y-auto divide-y divide-slate-800">
            {loading && results.length === 0 && (
              <div className="px-3 py-3 text-sm text-slate-500 font-mono" data-testid="guess-loading">Searching…</div>
            )}
            {showEmptyState && (
              <div className="px-3 py-3 text-sm text-slate-500" data-testid="guess-no-results">
                No matching wrestlers
              </div>
            )}
            {results.map((w, i) => (
              <button
                key={w.id}
                role="option"
                aria-selected={i === hi}
                data-idx={i}
                onMouseDown={(e) => { e.preventDefault(); pick(w); }}
                onMouseEnter={() => setHi(i)}
                className={`w-full text-left px-3 py-3 min-h-[48px] ${
                  i === hi ? "bg-red-950/40 text-white" : "text-slate-200 hover:bg-slate-800/80"
                }`}
                data-testid={`guess-option-${i}`}>
                <div className="font-semibold truncate">{w.primary_name}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mt-3">
        <Button
          onClick={submit}
          disabled={disabled || !selected}
          data-testid="submit-guess-btn"
          className="flex-1 sm:flex-initial h-12 px-6 bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white font-bold uppercase tracking-wide">
          {selected ? `Submit "${selected.primary_name}"` : "Pick from dropdown"}
        </Button>
        <Button variant="outline" onClick={onSkip} disabled={disabled}
          data-testid="skip-btn"
          className="h-12 px-4 border-slate-700 text-slate-200 hover:bg-slate-800">
          Skip
        </Button>
      </div>
    </div>
  );
}
